import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import EditWrap from "../commonComponent/editWrap";
import { Autocomplete, Button, Checkbox, Chip, Grid, TextField, Toolbar, Tooltip, Typography } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { BASE_URL } from "../../global";
import { toast } from "react-toastify";
import moment from "moment";
import { notifiy } from "../notification/Notification";

export const priorityOptions = [
  { id: 1, label: "High" },
  { id: 2, label: "Medium" },
  { id: 3, label: "Low" },
];

// Build the display label used both for showing and saving
const getDUCLabel = (inst) => `${inst.instrumentName || ""}${inst.DUCID ? ` - DUCID(${inst.DUCID})` : ""}${inst.certificateNumber ? ` - Certificate No (${inst.certificateNumber})` : ""}`;

const EditTaskMgmt = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isEdit = params?.id && params.id !== "0";

  const [inputStates, setInputStates] = useState({});
  const [clientArray, setClientArray] = useState([]);
  const [userList, setUserList] = useState([]);
  const [srfList, setSrfList] = useState([]);
  const [srfInstruments, setSrfInstruments] = useState([]);
  const [selectedDUCIDs, setSelectedDUCIDs] = useState([]);
  const [pendingInstrumentDetails, setPendingInstrumentDetails] = useState(null);
  const [settings, setSettings] = useState([]);

  const updateInputObject = (key, value) =>
    setInputStates((prev) => ({ ...prev, [key]: value }));

  const editAccess = localStorage.getItem("editAccess")?.split(",").map(Number);

  // Derived inline — no useEffect/state needed, always fresh
  const filteredSrfList = inputStates.clientId
    ? srfList.filter((s) => s.clientId === inputStates.clientId)
    : [];

  // ── Fetches ───────────────────────────────────────────────────────────────

  const fetchClients = () => {
    axiosWithToken
      .get(BASE_URL + "clients?_where=(status,eq,1)&_fields=id,companyName,uniCode&_sort=companyName")
      .then((res) =>
        setClientArray(
          (res.data || []).map((c) => ({
            id: c.id,
            label: `${c.id}, ${c.companyName}${c.uniCode ? ` (${c.uniCode})` : ""}`,
            companyName: c.companyName,
          }))
        )
      )
      .catch((err) => console.error("Failed to fetch clients", err));
  };

  const fetchUsers = () => {
    axiosWithToken
      .post(BASE_URL + "dynamic", {
        query: "select id, userName, emailId from users where status = 1 and type != 3",
      })
      .then((res) =>
        setUserList((res.data || []).map((u) => ({ id: u.id, label: u.userName, emailId: u.emailId })))
      )
      .catch((err) => console.error("Failed to fetch users", err));
  };

  const fetchSettings = () => {
    axiosWithToken
      .post(BASE_URL + "dynamic", {
        query: "select * from settings where status = 1",
      })
      .then((res) =>
        setSettings((res.data || []).map((u) => ({ id: u.id, label: u.keyName, value: u.value })))
      )
      .catch((err) => console.error("Failed to fetch users", err));
  };

  const fetchSrfList = () => {
    axiosWithToken
      .get(BASE_URL + "srfs?_where=(status,eq,1)&_fields=id,serviceReqNumber,clientId")
      .then((res) => setSrfList(res.data || []))
      .catch((err) => console.error("Failed to fetch SRFs", err));
  };

  const fetchSrfInstruments = (srfsId, savedDetails = null) => {
    if (!srfsId) { setSrfInstruments([]); return; }
    axiosWithToken
      .post(BASE_URL + "dynamic", {
        query: `SELECT si.*, i.instrumentName, c.certificateNumber FROM srfInstruments si LEFT JOIN instruments i ON i.id = si.instrumentId LEFT JOIN certificates c ON c.id = si.id AND c.status = 1 WHERE si.srfsId = ${srfsId} AND si.status = 1`,
      })
      .then((res) => {
        const instruments = res.data || [];
        setSrfInstruments(instruments);

        // Restore saved selections once instruments are loaded
        if (savedDetails) {
          const savedLabels = savedDetails.split(",").map((s) => s.trim()).filter(Boolean);
          // Match saved labels against loaded instruments
          const matched = instruments
            .filter((inst) => savedLabels.includes(getDUCLabel(inst)))
            .map((inst) => getDUCLabel(inst));
          setSelectedDUCIDs(matched);
        }
      })
      .catch((err) => console.error("Failed to fetch SRF instruments", err));
  };

  const fetchTaskDetails = () => {
    axiosWithToken
      .get(BASE_URL + `taskManagement/${params.id}`)
      .then((res) => {
        const resultData = res.data?.[0];
        setInputStates({
          clientId:          resultData.clientId          ?? null,
          serviceRequestNo:  resultData.serviceRequestNo  ?? null,
          Priority:          resultData.Priority          ?? null,
          reason:            resultData.reason            ?? null,
          completionDate:    resultData.completionDate    ?? null,
          assignedUser:      resultData.assignedUser      ?? null,
        });
        // Save pending details — will be resolved after srfList loads
        // and we can look up the srfId from serviceReqNumber
        setPendingInstrumentDetails(resultData.instrumentDetails || null);
      })
      .catch((err) => console.error("Failed to fetch task details", err));
  };

  useEffect(() => {
    fetchClients();
    fetchSrfList();
    fetchUsers();
    fetchSettings();
    if (isEdit) fetchTaskDetails();
  }, []);

  // Runs when ALL THREE are ready: srfList, serviceRequestNo, pendingInstrumentDetails
  useEffect(() => {
    if (!isEdit) return;
    if (!inputStates.serviceRequestNo || srfList.length === 0) return;
    // pendingInstrumentDetails can be null (no instruments saved) — that's fine, still fetch
    const matchedSrf = srfList.find((s) => s.serviceReqNumber == inputStates.serviceRequestNo);
    if (matchedSrf) {
      fetchSrfInstruments(matchedSrf.id, pendingInstrumentDetails);
    }
  }, [srfList, inputStates.serviceRequestNo, pendingInstrumentDetails]);


  const handleSubmit = async () => {
    const { clientId, serviceRequestNo, Priority, reason, completionDate, assignedUser } = inputStates;

    if (!clientId) {
      toast.error("Please select Client Name.");
      return;
    }

    if (!serviceRequestNo) {
      toast.error("Please enter Service Request No.");
      return;
    }

    if (!Priority) {
      toast.error("Please select Priority.");
      return;
    }

    if (!assignedUser) {
      toast.error("Please select Assigned User.");
      return;
    }
    const taskAssignEmailCC = settings.find((s) => s.label === "TaskAssignEmailCC")?.value;

    const payload = {
      clientId,
      serviceRequestNo,
      Priority,
      reason: reason || null,
      completionDate: completionDate ? moment(completionDate).format("YYYY-MM-DD HH:mm:ss") : null,
      assignedUser,
      instrumentDetails: selectedDUCIDs.length > 0 ? selectedDUCIDs.join(",") : null,
    };

    try {
      if (isEdit) {
        await axiosWithToken.patch(BASE_URL + `taskManagement/${params.id}`, payload);
        toast.success("Task updated successfully.");
        window.location.reload();
      } else {
        await axiosWithToken.post(BASE_URL + "taskManagement", payload);
        const selectedClient = clientArray.find((c) => c.id === clientId);
        const clientName = selectedClient?.companyName || clientId;
        const priorityLabel = Priority === 1 ? "High" : Priority === 2 ? "Medium" : "Low";
        const instrumentText = selectedDUCIDs?.length ? selectedDUCIDs.join(", ") : "N/A";
        const completionText = completionDate ? moment(completionDate).format("YYYY-MM-DD HH:mm") : "-";
        const deadlineDate = completionDate ? moment(completionDate).format("YYYY-MM-DD") : "-";
        const deadlineTime = completionDate ? moment(completionDate).format("HH:mm A") : "-";
        const notification = {
          _from: localStorage.getItem("id"),
          _to: assignedUser,
          body: `Task Assigned

Client: ${clientName}
Service Request No: ${serviceRequestNo}
Priority: ${priorityLabel}

Instrument Details: ${instrumentText}

Deadline Date: ${deadlineDate}

Deadline Time: ${deadlineTime}

Reason: ${reason || "-"}`.trim(),
          type: 1,
        };
        notifiy(notification, () => {
          console.log(`Notified of task to assigned user ${assignedUser}`);
        });
        const assignedUserObj = userList.find((u) => u.id === assignedUser);
        if (assignedUserObj?.emailId) {
          const emailPayload = {
            userEmail: assignedUserObj.emailId,
            clientName: clientName,
            subject: `LIMS - Task Assignment`,
            cc: taskAssignEmailCC || "",
            description: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
                <div style="background: #1976d2; padding: 20px 24px;">
                  <h2 style="color: #ffffff; margin: 0; font-size: 20px;">📋 Task Assigned</h2>
                </div>
                <div style="padding: 24px; background: #ffffff;">
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; width: 40%; color: #666; font-size: 14px; font-weight: bold;">Client</td>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #222; font-size: 14px; font-transform: capitalize;">${clientName}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666; font-size: 14px; font-weight: bold;">Service Request No.</td>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #222; font-size: 14px;">${serviceRequestNo}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666; font-size: 14px; font-weight: bold;">Priority</td>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; font-size: 14px;">
                        <span style="background: ${Priority === 1 ? "#ffebee; color: #c62828" : Priority === 2 ? "#fff8e1; color: #f57f17" : "#e8f5e9; color: #2e7d32"}; padding: 3px 10px; border-radius: 12px; font-size: 13px; font-weight: bold;">${priorityLabel}</span>
                      </td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666; font-size: 14px; font-weight: bold;">Instrument Details</td>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #222; font-size: 14px;">${instrumentText}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666; font-size: 14px; font-weight: bold;">Deadline Date</td>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #222; font-size: 14px;">
                        ${deadlineDate}
                      </td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666; font-size: 14px; font-weight: bold;">Deadline Time</td>
                      <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #222; font-size: 14px;">
                        ${deadlineTime}
                      </td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0; color: #666; font-size: 14px; font-weight: bold;">Reason</td>
                      <td style="padding: 10px 0; color: #222; font-size: 14px;">${reason || "-"}</td>
                    </tr>
                  </table>
                </div>
              </div>
            `,
          };
          axiosWithToken.post(BASE_URL + "email", emailPayload)
          .then((res) => console.log("Email sent successfully", res))
          .catch((err) => console.error("Email send failed", err));
        }
        toast.success("Task created successfully.");
        navigate("/taskMgmtList");
      }
    } catch (err) {
      console.error("Failed to save task", err);
    }
  };

  return (
    <EditWrap>
      <Typography variant="h6" component="h6" sx={{ float: "left" }}>
        {isEdit ? "Edit Task" : "Create Task"}
      </Typography>
      <br />

      <Grid container spacing={2}>
        {/* Client */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={clientArray}
            value={clientArray.find((o) => o.id === inputStates.clientId) || null}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(opt) => opt.label || ""}
            onChange={(_, val) => {
              updateInputObject("clientId", val?.id ?? null);
              updateInputObject("serviceRequestNo", null);
              setSrfInstruments([]);
              setSelectedDUCIDs([]);
            }}
            renderInput={(params) => <TextField {...params} label="Client *" />}
            disabled={isEdit}
          />
        </Grid>

        {/* SRF */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={filteredSrfList}
            value={filteredSrfList.find((s) => s.serviceReqNumber === inputStates.serviceRequestNo) || null}
            isOptionEqualToValue={(opt, val) => opt.serviceReqNumber === val?.serviceReqNumber}
            getOptionLabel={(opt) => opt.serviceReqNumber || ""}
            onChange={(_, val) => {
              updateInputObject("serviceRequestNo", val?.serviceReqNumber ?? null);
              setSelectedDUCIDs([]);
              fetchSrfInstruments(val?.id ?? null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="SRF No. *"
                placeholder={!inputStates.clientId ? "Select a client first" : ""}
              />
            )}
            disabled={isEdit}
          />
        </Grid>

        {/* Instrument Details — multi-select DUC */}
        <Grid item xs={12} sm={12} md={12} lg={6}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={srfInstruments}
            value={srfInstruments.filter((inst) => selectedDUCIDs.includes(getDUCLabel(inst)))}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(opt) => getDUCLabel(opt)}
            onChange={(_, vals) => setSelectedDUCIDs(vals.map((v) => getDUCLabel(v)))}
            disabled={srfInstruments.length === 0}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {getDUCLabel(option)}
              </li>
            )}
            renderTags={(vals, getTagProps) =>
              vals.map((opt, index) => (
                <Chip
                  key={opt.id}
                  label={getDUCLabel(opt)}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Instrument Details"
                placeholder={srfInstruments.length === 0 ? "Select SRF first" : ""}
              />
            )}
            disabled={isEdit}
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={priorityOptions}
            value={priorityOptions.find((p) => p.id === inputStates.Priority) || null}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(opt) => opt.label || ""}
            onChange={(_, val) => updateInputObject("Priority", val?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Task Priority *" />
            )}
            disabled={isEdit}
          />
        </Grid>

        {/* Assign User */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={userList}
            value={userList.find((u) => u.id === inputStates.assignedUser) || null}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            getOptionLabel={(opt) => opt.label || ""}
            onChange={(_, val) => updateInputObject("assignedUser", val?.id ?? null)}
            renderInput={(params) => (
              <TextField {...params} label="Assign To *" />
            )}
          />
        </Grid>

        {/* Reason */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            size="small"
            fullWidth
            label="Reason"
            value={inputStates.reason || ""}
            multiline
            onChange={(e) => updateInputObject("reason", e.target.value)}
          />
        </Grid>

        {/* Completion Date */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              format="dd/MM/yyyy hh:mm a"
              value={inputStates.completionDate ? new Date(inputStates.completionDate) : null}
              onChange={(val) => updateInputObject("completionDate", val)}
              slotProps={{
                textField: { size: "small", fullWidth: true, label: "Completion Date & Time", },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginTop: "16px",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?"))
              window.history.back();
          }}
        >
          Cancel
        </Button>

        <Tooltip
          title={
            !(editAccess?.includes(0) || editAccess?.includes(1))
              ? "You don't have access"
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              size="small"
              disabled={!(editAccess?.includes(0) || editAccess?.includes(1))}
              onClick={handleSubmit}
            >
              {isEdit ? "Update" : "Save"}
            </Button>
          </span>
        </Tooltip>
      </Toolbar>
    </EditWrap>
  );
};

export default EditTaskMgmt;